/* Processus — W14-ADD Process Intelligence & Operational Graph.
 * Même grammaire visuelle que le graphe ontologique (fond sombre, nœuds lumineux,
 * relations nommées). Projection de la vérité NOAM — jamais une seconde vérité.
 * Les autres graphes du site ne sont PAS modifiés. */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ReactFlow, Background, Controls, MarkerType } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { Loader2, Workflow, PenLine, Mic, Upload, Download, Plus, CheckCircle2, AlertTriangle } from "lucide-react";

const TYPE_STYLE = {
  process:    { color: "#EFD127", bg: "#3a2f00", label: "Processus" },
  step:       { color: "#52B0E0", bg: "#0e2d3d", label: "Étape" },
  person:     { color: "#f4f4f5", bg: "#26262b", label: "Personne" },
  control:    { color: "#1D9E75", bg: "#0a2d22", label: "Contrôle" },
  risk:       { color: "#E24B4A", bg: "#391010", label: "Risque" },
  document:   { color: "#EF9F27", bg: "#3a2810", label: "Document" },
  system:     { color: "#7F77DD", bg: "#1d1a3a", label: "Système" },
  data:       { color: "#4A89DC", bg: "#0d1f3a", label: "Donnée" },
  decision:   { color: "#1D9E75", bg: "#0a2d22", label: "Décision" },
  obligation: { color: "#EF9F27", bg: "#3a2810", label: "Obligation" },
  finding:    { color: "#E24B4A", bg: "#391010", label: "Constat" },
  input:      { color: "#8b8b94", bg: "#1e1e22", label: "Entrée" },
  output:     { color: "#8b8b94", bg: "#1e1e22", label: "Sortie" },
  exception:  { color: "#E24B4A", bg: "#2a1414", label: "Exception" },
};
const ROUTE_BY_REF = { risk: "/app/risques", control: "/app/controles", obligation: "/app/gouvernance", decision: "/app/decisions", document: "/app/documents" };

function Node({ data }) {
  const s = TYPE_STYLE[data.type] || TYPE_STYLE.data;
  const main = data.type === "process" || data.type === "step";
  return (
    <div data-testid={`process-node-${data.type}`} style={{
      background: s.bg, border: `1.5px ${data.inferred ? "dashed" : "solid"} ${s.color}`,
      borderRadius: data.type === "process" ? 999 : 8, padding: main ? "10px 16px" : "6px 10px",
      maxWidth: 200, boxShadow: main ? `0 0 18px ${s.color}55` : "none", cursor: "pointer",
    }}>
      <div style={{ fontSize: 8, textTransform: "uppercase", letterSpacing: 1, color: s.color, marginBottom: 2 }}>
        {s.label}{data.inferred ? ` · inféré ${Math.round((data.confidence || 0) * 100)}%` : ""}
      </div>
      <div style={{ fontSize: main ? 12 : 10, fontWeight: main ? 700 : 500, color: "#f4f4f5", lineHeight: 1.25 }}>{data.label}</div>
    </div>
  );
}
const nodeTypes = { noam: Node };

function layout(graph) {
  const pos = {}; const steps = graph.nodes.filter((n) => n.type === "step");
  graph.nodes.forEach((n) => {
    if (n.type === "process") pos[n.id] = { x: 0, y: 0 };
  });
  steps.forEach((s, i) => { pos[s.id] = { x: (i + 1) * 300, y: 160 }; });
  const satByStep = {};
  graph.edges.forEach((e) => {
    const from = graph.nodes.find((n) => n.id === e.from);
    if (from && from.type === "step" && !pos[e.to]) {
      satByStep[e.from] = satByStep[e.from] || [];
      satByStep[e.from].push(e.to);
    }
  });
  Object.entries(satByStep).forEach(([sid, sats]) => {
    sats.forEach((id, i) => {
      pos[id] = { x: pos[sid].x - 90 + (i % 3) * 160, y: pos[sid].y + 140 + Math.floor(i / 3) * 90 };
    });
  });
  graph.nodes.forEach((n, i) => { if (!pos[n.id]) pos[n.id] = { x: i * 150, y: -160 }; });
  return pos;
}

const ELEMENT_KEYS = ["controls", "risks", "documents", "systems", "data", "decisions", "obligations", "inputs", "outputs", "exceptions"];

function Badge({ el }) {
  return el.inferred
    ? <span className="text-[9px] px-1 rounded bg-amber-950 text-amber-400 border border-amber-800">inféré {Math.round((el.confidence || 0) * 100)}%</span>
    : <span className="text-[9px] px-1 rounded bg-emerald-950 text-emerald-400 border border-emerald-800">établi</span>;
}

function Builder({ onClose, onCreated }) {
  const [mode, setMode] = useState("write");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  const fileRef = useRef(null);

  const speak = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { notify.error("Dictée non supportée par ce navigateur — utilisez « Décrire à l'écrit »."); return; }
    if (listening) { recRef.current?.stop(); setListening(false); return; }
    const rec = new SR(); rec.lang = "fr-FR"; rec.continuous = true; rec.interimResults = false;
    rec.onresult = (e) => setText((t) => t + " " + Array.from(e.results).slice(e.resultIndex).map((r) => r[0].transcript).join(" "));
    rec.onend = () => setListening(false);
    recRef.current = rec; rec.start(); setListening(true); setMode("speak");
  };

  const extract = async (file) => {
    setBusy(true);
    try {
      let r;
      if (file) {
        const fd = new FormData(); fd.append("file", file);
        r = await api.post("/workspace/processes/extract-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        r = await api.post("/workspace/processes/extract", { text, source_label: mode === "speak" ? "transcription orale" : "description écrite" });
      }
      setDraft(r.data);
    } catch (e) { notify.error(fmtError(e)); }
    setBusy(false);
  };

  const materialize = async () => {
    setBusy(true);
    try {
      const r = await api.post("/workspace/processes", {
        name: draft.name, description: draft.description, created_via: mode,
        source_label: draft.source_label, actors: draft.actors || [], steps: draft.steps || [],
      });
      notify.success("Processus matérialisé — objets reliés à la vérité NOAM.");
      onCreated(r.data);
    } catch (e) { notify.error(fmtError(e)); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto p-6" data-testid="process-builder-modal">
      <div className="noam-card bg-[#101013] border border-zinc-800 rounded-xl p-6 w-full max-w-3xl space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-lg font-semibold text-zinc-100">Générer un processus avec NOAM</div>
          <button data-testid="process-builder-close" onClick={onClose} className="text-zinc-500 hover:text-zinc-200">✕</button>
        </div>
        {!draft && (
          <>
            <div className="grid grid-cols-3 gap-3">
              <button data-testid="builder-mode-write" onClick={() => setMode("write")}
                className={`p-3 rounded-lg border text-left ${mode === "write" ? "border-sky-500 bg-sky-950/30" : "border-zinc-800"}`}>
                <PenLine size={16} className="text-sky-400 mb-1" /><div className="text-sm text-zinc-200">Décrire à l'écrit</div>
              </button>
              <button data-testid="builder-mode-speak" onClick={speak}
                className={`p-3 rounded-lg border text-left ${listening ? "border-red-500 bg-red-950/30 animate-pulse" : "border-zinc-800"}`}>
                <Mic size={16} className="text-red-400 mb-1" /><div className="text-sm text-zinc-200">{listening ? "En écoute… (cliquer pour stopper)" : "Parler à NOAM"}</div>
              </button>
              <button data-testid="builder-mode-import" onClick={() => fileRef.current?.click()}
                className="p-3 rounded-lg border border-zinc-800 text-left">
                <Upload size={16} className="text-amber-400 mb-1" /><div className="text-sm text-zinc-200">Importer une procédure</div>
                <input ref={fileRef} type="file" hidden accept=".pdf,.docx,.txt,.md,.pptx"
                  onChange={(e) => e.target.files?.[0] && extract(e.target.files[0])} />
              </button>
            </div>
            <textarea data-testid="process-description-input" value={text} onChange={(e) => setText(e.target.value)}
              rows={7} placeholder="Décrivez le processus : étapes, acteurs, systèmes utilisés, contrôles, risques, échéances…"
              className="w-full bg-zinc-950 border border-zinc-800 rounded-lg p-3 text-sm text-zinc-200" />
            <button data-testid="process-extract-btn" disabled={busy || text.trim().length < 30} onClick={() => extract()}
              className="px-4 py-2 rounded-lg bg-sky-600 hover:bg-sky-500 text-white text-sm disabled:opacity-40">
              {busy ? <Loader2 className="animate-spin inline" size={14} /> : "Extraire et structurer"}
            </button>
          </>
        )}
        {draft && (
          <div className="space-y-3" data-testid="process-draft-review">
            <div className="text-sm text-zinc-400">NOAM propose — validez avant matérialisation. Rien n'est inventé :
              chaque élément est <span className="text-emerald-400">établi (evidence verbatim)</span> ou <span className="text-amber-400">inféré (incertitude assumée)</span>.</div>
            <div className="text-base font-semibold text-zinc-100">{draft.name}</div>
            <div className="text-xs text-zinc-500">{draft.description}</div>
            <div className="text-xs text-zinc-500">Extraction : {draft.extraction_stats?.established ?? 0} établis · {draft.extraction_stats?.inferred ?? 0} inférés · {draft.extraction_stats?.evidence_rejected ?? 0} evidences rejetées</div>
            <div className="max-h-72 overflow-y-auto space-y-2">
              {(draft.steps || []).map((s, i) => (
                <div key={i} className="border border-zinc-800 rounded-lg p-2">
                  <div className="text-sm text-zinc-200 flex items-center gap-2">{i + 1}. {s.name} <Badge el={s} /></div>
                  <div className="text-[11px] text-zinc-500">Responsable : {s.responsible || "— (non assigné)"} {s.due_or_sla ? `· SLA ${s.due_or_sla}` : ""}</div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {ELEMENT_KEYS.flatMap((k) => (s[k] || []).map((el, j) => (
                      <span key={k + j} className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-900 border border-zinc-800 text-zinc-300">
                        {TYPE_STYLE[k.replace(/s$/, "")]?.label || k} · {el.label} <Badge el={el} />
                      </span>
                    )))}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <button data-testid="process-materialize-btn" disabled={busy} onClick={materialize}
                className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm">
                {busy ? <Loader2 className="animate-spin inline" size={14} /> : "Valider et matérialiser"}
              </button>
              <button data-testid="process-draft-discard" onClick={() => setDraft(null)} className="px-4 py-2 rounded-lg border border-zinc-700 text-zinc-300 text-sm">Reprendre</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Processus() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const [graph, setGraph] = useState(null);
  const [selNode, setSelNode] = useState(null);
  const [builder, setBuilder] = useState(false);
  const [findingForm, setFindingForm] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/workspace/processes");
      setItems(r.data.items || []);
      if ((r.data.items || []).length && !sel) setSel(r.data.items[0]);
    } catch (e) { notify.error(fmtError(e)); }
    setLoading(false);
  }, [sel]);
  useEffect(() => { load(); }, []); // eslint-disable-line

  useEffect(() => {
    if (!sel) { setGraph(null); return; }
    api.get(`/workspace/processes/${sel.id}/graph`).then((r) => setGraph(r.data)).catch(() => setGraph(null));
  }, [sel]);

  const { nodes, edges } = useMemo(() => {
    if (!graph) return { nodes: [], edges: [] };
    const pos = layout(graph);
    return {
      nodes: graph.nodes.map((n) => ({ id: n.id, type: "noam", position: pos[n.id], data: n })),
      edges: graph.edges.map((e, i) => ({
        id: `e${i}`, source: e.from, target: e.to, label: e.label,
        style: { stroke: "#3f3f46" }, labelStyle: { fill: "#8b8b94", fontSize: 8, fontStyle: "italic" },
        labelBgStyle: { fill: "#0b0b0d" }, markerEnd: { type: MarkerType.ArrowClosed, color: "#3f3f46" },
      })),
    };
  }, [graph]);

  const onNodeClick = (_, node) => {
    setSelNode(node.data);
    if (node.data.ref_type && ROUTE_BY_REF[node.data.ref_type]) return; // navigation via bouton du panneau
  };

  const submitFinding = async (e) => {
    e.preventDefault();
    const f = Object.fromEntries(new FormData(e.target).entries());
    try {
      await api.post(`/workspace/processes/${sel.id}/findings`, {
        step_id: findingForm.stepId || null, title: f.title, severity: Number(f.severity || 3),
        action_title: f.action_title, owner_email: f.owner_email || null, due_at: f.due_at ? `${f.due_at}T00:00:00Z` : null,
        evidence: f.evidence || null,
      });
      notify.success("Constat relié au processus — action corrective créée (P-1).");
      setFindingForm(null);
      const r = await api.get(`/workspace/processes/${sel.id}/graph`); setGraph(r.data);
    } catch (err) { notify.error(fmtError(err)); }
  };

  return (
    <div className="space-y-4" data-testid="processes-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100 flex items-center gap-2"><Workflow size={18} className="text-[#7F77DD]" /> Cartographie des processus</h1>
          <p className="text-xs text-zinc-500">Visualisez votre organisation comme un réseau vivant — projection de la vérité NOAM.</p>
        </div>
        <div className="flex gap-2">
          {sel && (
            <a data-testid="process-export-bpmn" href={`${api.defaults.baseURL}/workspace/processes/${sel.id}/export/bpmn`}
              className="px-3 py-2 rounded-lg border border-zinc-700 text-zinc-300 text-xs flex items-center gap-1">
              <Download size={13} /> BPMN 2.0 (.bpmn, importable Visio)
            </a>
          )}
          <button data-testid="process-generate-btn" onClick={() => setBuilder(true)}
            className="px-3 py-2 rounded-lg bg-[#7F77DD] hover:bg-[#6c63d0] text-white text-xs flex items-center gap-1">
            <Plus size={13} /> Générer avec NOAM
          </button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="process-list">
        {loading ? <Loader2 className="animate-spin text-zinc-500" /> : items.length === 0 ? (
          <div className="text-sm text-zinc-500 border border-dashed border-zinc-800 rounded-lg p-6 w-full text-center">
            Aucun processus décrit. Commencez par « Générer avec NOAM » : à l'écrit, à l'oral, ou en important une procédure existante.
          </div>
        ) : items.map((p) => (
          <button key={p.id} data-testid={`process-item-${p.id}`} onClick={() => { setSel(p); setSelNode(null); }}
            className={`px-3 py-1.5 rounded-full text-xs border ${sel?.id === p.id ? "border-[#EFD127] text-[#EFD127] bg-[#3a2f00]/40" : "border-zinc-800 text-zinc-300"}`}>
            {p.name} · {(p.steps || []).length} étapes{(p.findings || []).filter((f) => f.status === "open").length ? ` · ${(p.findings || []).filter((f) => f.status === "open").length} constat(s)` : ""}
          </button>
        ))}
      </div>

      {sel && (
        <div className="flex gap-3" style={{ height: 560 }}>
          <div className="flex-1 rounded-xl border border-zinc-800 overflow-hidden" style={{ background: "#0b0b0d" }} data-testid="operational-graph">
            <ReactFlow key={sel.id} nodes={nodes} edges={edges} nodeTypes={nodeTypes} onNodeClick={onNodeClick}
              fitView fitViewOptions={{ padding: 0.25 }} minZoom={0.15} proOptions={{ hideAttribution: true }}>
              <Background color="#1c1c21" gap={28} />
              <Controls showInteractive={false} />
            </ReactFlow>
          </div>
          <div className="w-80 shrink-0 rounded-xl border border-zinc-800 bg-[#101013] p-4 overflow-y-auto" data-testid="process-side-panel">
            {!selNode ? (
              <div className="text-xs text-zinc-500">Cliquez un nœud : étape, responsable, contrôle, risque, document, constat… Chaque nœud renvoie à l'objet réel NOAM.</div>
            ) : (
              <div className="space-y-3">
                <div className="text-[10px] uppercase tracking-wider" style={{ color: (TYPE_STYLE[selNode.type] || {}).color }}>
                  {(TYPE_STYLE[selNode.type] || {}).label}{selNode.inferred ? " · inféré" : " · établi"}
                </div>
                <div className="text-sm font-semibold text-zinc-100">{selNode.label}</div>
                {selNode.status && <div className="text-xs text-zinc-400">Statut : {selNode.status}</div>}
                {selNode.curation === "unlinked" && (
                  <div className="text-xs text-amber-400 flex items-center gap-1"><AlertTriangle size={12} /> Décrit dans le processus, introuvable au registre — à créer ou relier (open world : conservé, jamais rejeté).</div>
                )}
                {selNode.ref_type && ROUTE_BY_REF[selNode.ref_type] && (
                  <button data-testid="process-node-open-ref" onClick={() => nav(ROUTE_BY_REF[selNode.ref_type])}
                    className="px-3 py-1.5 rounded-lg border border-zinc-700 text-zinc-200 text-xs w-full">Ouvrir l'objet réel dans NOAM →</button>
                )}
                {selNode.type === "step" && (
                  <button data-testid="process-add-finding-btn" onClick={() => setFindingForm({ stepId: selNode.id })}
                    className="px-3 py-1.5 rounded-lg bg-red-900/40 border border-red-800 text-red-300 text-xs w-full">+ Ajouter un constat d'audit sur cette étape</button>
                )}
                {selNode.type === "finding" && selNode.status === "open" && (
                  <button data-testid="process-remediate-btn" onClick={async () => {
                    const proof = window.prompt("Preuve de remédiation :");
                    if (!proof) return;
                    try {
                      await api.post(`/workspace/processes/findings/${selNode.id}/remediate`, { proof });
                      notify.success("Constat remédié — obligation P-1 résolue.");
                      const r = await api.get(`/workspace/processes/${sel.id}/graph`); setGraph(r.data); setSelNode(null);
                    } catch (e) { notify.error(fmtError(e)); }
                  }} className="px-3 py-1.5 rounded-lg bg-emerald-900/40 border border-emerald-800 text-emerald-300 text-xs w-full flex items-center justify-center gap-1">
                    <CheckCircle2 size={13} /> Marquer remédié (preuve requise)
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {findingForm && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6">
          <form onSubmit={submitFinding} className="noam-card bg-[#101013] border border-zinc-800 rounded-xl p-5 w-full max-w-md space-y-3" data-testid="finding-form">
            <div className="text-sm font-semibold text-zinc-100">Constat d'audit — relié Processus → Étape → Action</div>
            <input name="title" required placeholder="Constat (faiblesse identifiée)" className="w-full bg-zinc-950 border border-zinc-800 rounded p-2 text-sm text-zinc-200" data-testid="finding-title-input" />
            <textarea name="evidence" placeholder="Evidence (facultatif)" rows={2} className="w-full bg-zinc-950 border border-zinc-800 rounded p-2 text-xs text-zinc-300" />
            <input name="action_title" required placeholder="Action corrective" className="w-full bg-zinc-950 border border-zinc-800 rounded p-2 text-sm text-zinc-200" data-testid="finding-action-input" />
            <div className="flex gap-2">
              <select name="severity" defaultValue="3" className="bg-zinc-950 border border-zinc-800 rounded p-2 text-sm text-zinc-300">
                {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>Sévérité {n}</option>)}
              </select>
              <input name="owner_email" placeholder="Responsable (email)" className="flex-1 bg-zinc-950 border border-zinc-800 rounded p-2 text-sm text-zinc-200" />
              <input name="due_at" type="date" className="bg-zinc-950 border border-zinc-800 rounded p-2 text-sm text-zinc-300" />
            </div>
            <div className="flex gap-2">
              <button type="submit" data-testid="finding-submit-btn" className="px-4 py-2 rounded-lg bg-red-700 hover:bg-red-600 text-white text-sm">Créer le constat + l'action P-1</button>
              <button type="button" onClick={() => setFindingForm(null)} className="px-4 py-2 rounded-lg border border-zinc-700 text-zinc-300 text-sm">Annuler</button>
            </div>
          </form>
        </div>
      )}

      {builder && <Builder onClose={() => setBuilder(false)} onCreated={(p) => { setBuilder(false); setItems((xs) => [p, ...xs]); setSel(p); }} />}
    </div>
  );
}
